/**
 * 🎯 MISSION 9 - PHASE 3: ENGAGEMENT ANALYTICS COMPONENT (AGGREGATE ONLY)
 * Sessions, feature usage, time-in-app metrics
 */

import React, { useState, useEffect } from 'react';
import {
  Activity,
  Users,
  Clock,
  TrendingUp,
  MessageSquare,
  Target,
  Calendar,
  Zap,
  RefreshCw,
  BarChart3
} from 'lucide-react';

interface EngagementOverview {
  overview: {
    total_sessions: number;
    unique_users: number;
    avg_session_minutes: number;
    total_hours: number;
    sessions_per_user: number;
  };
  active_users: {
    dau: number;
    wau: number;
    dau_wau_ratio: number;
  };
  daily_trends: Array<{
    date: string;
    sessions: number;
    active_users: number;
    avg_session_minutes: number;
  }>;
}

interface FeatureUsage {
  feature_summary: {
    chat: {
      total_messages: number;
      unique_users: number;
      avg_message_length: number;
    };
    goals: {
      goals_created: number;
      unique_users: number;
      active_goals: number;
      completed_goals: number;
    };
    plans: {
      plans_generated: number;
      unique_users: number;
      active_plans: number;
    };
    workouts: {
      workouts_completed: number;
      unique_users: number;
      avg_duration: number;
    };
    uploads: {
      files_uploaded: number;
      unique_users: number;
      images: number;
      tcx_files: number;
    };
    settings: {
      settings_changed: number;
      unique_users: number;
    };
  };
}

interface TimeInApp {
  overview: {
    total_hours: number;
    avg_minutes_per_session: number;
    unique_users: number;
    avg_hours_per_user: number;
  };
  distribution: {
    avg_minutes_per_user: number;
    max_minutes_per_user: number;
    min_minutes_per_user: number;
    median_minutes: number;
    p90_minutes: number;
  };
  peak_hours: Array<{
    hour: number;
    hour_label: string;
    session_count: number;
    unique_users: number;
    avg_duration_minutes: number;
  }>;
  day_distribution: Array<{
    day_of_week: number;
    day_name: string;
    session_count: number;
    unique_users: number;
    avg_duration_minutes: number;
  }>;
}

export const EngagementAnalytics: React.FC = () => {
  const [overview, setOverview] = useState<EngagementOverview | null>(null);
  const [featureUsage, setFeatureUsage] = useState<FeatureUsage | null>(null);
  const [timeInApp, setTimeInApp] = useState<TimeInApp | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewRes, featureRes, timeRes] = await Promise.all([
        fetch(`/api/admin/engagement/overview?days=${days}`),
        fetch(`/api/admin/engagement/feature-usage?days=${days}`),
        fetch(`/api/admin/engagement/time-in-app?days=${days}`)
      ]);

      const [overviewData, featureData, timeData] = await Promise.all([
        overviewRes.json(),
        featureRes.json(),
        timeRes.json()
      ]);

      setOverview(overviewData);
      setFeatureUsage(featureData);
      setTimeInApp(timeData);
    } catch (error) {
      console.error('Failed to fetch engagement analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (!overview || !featureUsage || !timeInApp) {
    return (
      <div className="text-center text-gray-500 py-12">
        <Activity className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <p>No engagement data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Engagement Analytics</h1>
          <p className="text-sm text-gray-600 mt-1">
            Aggregate metrics: sessions, features, time-in-app
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-4 py-2 border border-gray-300 rounded-lg"
          >
            <option value={1}>Last 24 hours</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={fetchAnalytics}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Sessions Overview */}
      <div className="grid grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {overview.overview.total_sessions.toLocaleString()}
              </p>
            </div>
            <Activity className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Unique Users</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {overview.overview.unique_users.toLocaleString()}
              </p>
            </div>
            <Users className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Session</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {overview.overview.avg_session_minutes.toFixed(1)}m
              </p>
            </div>
            <Clock className="h-8 w-8 text-purple-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Hours</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {overview.overview.total_hours.toFixed(1)}h
              </p>
            </div>
            <TrendingUp className="h-8 w-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Sessions/User</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {overview.overview.sessions_per_user.toFixed(1)}
              </p>
            </div>
            <BarChart3 className="h-8 w-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Active Users */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Users className="h-5 w-5 mr-2 text-blue-600" />
          Active Users (DAU / WAU)
        </h3>
        <div className="grid grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-600">Daily Active Users (DAU)</p>
            <p className="text-3xl font-bold text-blue-600">
              {overview.active_users.dau.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Weekly Active Users (WAU)</p>
            <p className="text-3xl font-bold text-purple-600">
              {overview.active_users.wau.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">DAU/WAU Ratio</p>
            <p className="text-3xl font-bold text-green-600">
              {overview.active_users.dau_wau_ratio.toFixed(1)}%
            </p>
            <p className="text-xs text-gray-500 mt-1">Stickiness metric</p>
          </div>
        </div>
      </div>

      {/* Feature Usage */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Zap className="h-5 w-5 mr-2 text-yellow-600" />
          Feature Usage Summary
        </h3>
        <div className="grid grid-cols-3 gap-6">
          {/* Chat */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              <h4 className="font-semibold text-gray-900">Chat</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Messages:</span>
                <span className="text-sm font-semibold">{featureUsage.feature_summary.chat.total_messages.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Users:</span>
                <span className="text-sm font-semibold">{featureUsage.feature_summary.chat.unique_users.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Avg Length:</span>
                <span className="text-sm font-semibold">{featureUsage.feature_summary.chat.avg_message_length.toFixed(0)} chars</span>
              </div>
            </div>
          </div>

          {/* Goals */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Target className="h-5 w-5 text-green-600" />
              <h4 className="font-semibold text-gray-900">Goals</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Created:</span>
                <span className="text-sm font-semibold">{featureUsage.feature_summary.goals.goals_created.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Active:</span>
                <span className="text-sm font-semibold text-green-600">{featureUsage.feature_summary.goals.active_goals.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Completed:</span>
                <span className="text-sm font-semibold text-blue-600">{featureUsage.feature_summary.goals.completed_goals.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Workouts */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Activity className="h-5 w-5 text-purple-600" />
              <h4 className="font-semibold text-gray-900">Workouts</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Completed:</span>
                <span className="text-sm font-semibold">{featureUsage.feature_summary.workouts.workouts_completed.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Users:</span>
                <span className="text-sm font-semibold">{featureUsage.feature_summary.workouts.unique_users.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Avg Duration:</span>
                <span className="text-sm font-semibold">{featureUsage.feature_summary.workouts.avg_duration.toFixed(0)}m</span>
              </div>
            </div>
          </div>

          {/* Plans */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Calendar className="h-5 w-5 text-yellow-600" />
              <h4 className="font-semibold text-gray-900">Plans</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Generated:</span>
                <span className="text-sm font-semibold">{featureUsage.feature_summary.plans.plans_generated.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Active:</span>
                <span className="text-sm font-semibold text-green-600">{featureUsage.feature_summary.plans.active_plans.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Users:</span>
                <span className="text-sm font-semibold">{featureUsage.feature_summary.plans.unique_users.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Uploads */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <TrendingUp className="h-5 w-5 text-red-600" />
              <h4 className="font-semibold text-gray-900">Uploads</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total:</span>
                <span className="text-sm font-semibold">{featureUsage.feature_summary.uploads.files_uploaded.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Images:</span>
                <span className="text-sm font-semibold">{featureUsage.feature_summary.uploads.images.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">TCX Files:</span>
                <span className="text-sm font-semibold">{featureUsage.feature_summary.uploads.tcx_files.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <Activity className="h-5 w-5 text-gray-600" />
              <h4 className="font-semibold text-gray-900">Settings</h4>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Changes:</span>
                <span className="text-sm font-semibold">{featureUsage.feature_summary.settings.settings_changed.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Users:</span>
                <span className="text-sm font-semibold">{featureUsage.feature_summary.settings.unique_users.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Time-in-App Stats */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Clock className="h-5 w-5 mr-2 text-purple-600" />
          Time-in-App Statistics
        </h3>
        <div className="grid grid-cols-4 gap-6 mb-6">
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-600">
              {timeInApp.overview.total_hours.toFixed(1)}h
            </p>
            <p className="text-sm text-gray-600 mt-1">Total Hours</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-blue-600">
              {timeInApp.overview.avg_hours_per_user.toFixed(2)}h
            </p>
            <p className="text-sm text-gray-600 mt-1">Avg Hours/User</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-green-600">
              {timeInApp.distribution.median_minutes.toFixed(0)}m
            </p>
            <p className="text-sm text-gray-600 mt-1">Median Minutes</p>
          </div>
          <div className="text-center">
            <p className="text-3xl font-bold text-yellow-600">
              {timeInApp.distribution.p90_minutes.toFixed(0)}m
            </p>
            <p className="text-sm text-gray-600 mt-1">90th Percentile</p>
          </div>
        </div>

        {/* Peak Hours */}
        <div className="mt-6">
          <h4 className="font-semibold text-gray-900 mb-3">Peak Usage Hours (Top 5)</h4>
          <div className="space-y-2">
            {timeInApp.peak_hours.slice(0, 5).map((hour, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 px-4 bg-gray-50 rounded">
                <span className="font-medium text-gray-900">{hour.hour_label}</span>
                <div className="flex items-center space-x-4 text-sm">
                  <span className="text-gray-600">{hour.session_count} sessions</span>
                  <span className="text-gray-600">{hour.unique_users} users</span>
                  <span className="text-purple-600 font-semibold">{hour.avg_duration_minutes.toFixed(1)}m avg</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Day Distribution */}
        <div className="mt-6">
          <h4 className="font-semibold text-gray-900 mb-3">Day of Week Distribution</h4>
          <div className="grid grid-cols-7 gap-2">
            {timeInApp.day_distribution.map((day, idx) => (
              <div key={idx} className="text-center border rounded-lg p-3">
                <p className="text-xs font-medium text-gray-600 mb-2">{day.day_name.slice(0, 3)}</p>
                <p className="text-lg font-bold text-gray-900">{day.session_count}</p>
                <p className="text-xs text-gray-500 mt-1">{day.unique_users} users</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngagementAnalytics;

